import { useMemo } from 'react';
import { usePlanets } from './use-planets';
import { useUsers } from './use-users';
import { useTransactions } from './use-transactions';
import { useTxByPlanet } from './use-tx-by-planet';
import { computeRiskItem } from '../utils/risk';
import type { Planet } from '../schemas/planet';

export interface HighRiskPlanet {
  planet: Planet;
  inProgressCount: number;
}

/**
 * Derives the list of HIGH-risk planets by reusing existing data hooks
 * (usePlanets, useUsers, useTransactions) and shared utils (computeRiskItem, useTxByPlanet).
 */
export function useHighRiskPlanets() {
  const { data: planets, isLoading: loadingPlanets } = usePlanets();
  const { data: users, isLoading: loadingUsers } = useUsers();
  const { data: transactions, isLoading: loadingTx } = useTransactions();

  const txByPlanet = useTxByPlanet(users, transactions);

  const highRiskPlanets = useMemo((): HighRiskPlanet[] => {
    if (!planets) return [];
    return planets
      .map(p => computeRiskItem(p, txByPlanet.get(p.id) ?? []))
      .filter(item => item.risk === 'HIGH')
      .map(item => ({ planet: item.entity, inProgressCount: item.inProgress }));
  }, [planets, txByPlanet]);

  return {
    highRiskPlanets,
    loading: loadingPlanets || loadingUsers || loadingTx,
  };
}
